import { useState, SyntheticEvent, MouseEvent, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { cloneDeep } from "lodash"
import classNames from "classnames"
import { Menu, MenuItem } from "@mui/material"

import PlayerGroupManageDialog from "@/app/settings/analysis-groups/players/PlayerGroupManageDialog"
import PlayerGroupDeleteDialog from "@/app/settings/analysis-groups/players/PlayerGroupDeleteDialog"
import PlayerGroupTabs from "@/app/settings/analysis-groups/players/PlayerGroupTabs"
import PlayerGroupTable from "@/app/settings/analysis-groups/players/components/PlayerGroupTable"
import IconButton from "@/components/button/IconButton"
import Search from "@/components/form/search/Search"
import { usePlayerGroupsQuery } from "@/hooks/api/useSettingQuery"
import { TeamPlayerGroup } from "@/types/setting"
import { createGlobalStore } from "@/stores/global"

import "./PlayerGroups.scss"

type TabItemType = {
  label: string
  value: number
  icon: number
}

const MAX_PLAYER_GROUP_COUNT = 50

export default function PlayerGroups() {
  const { t } = useTranslation("")
  const setSnackBar = createGlobalStore((state) => state.setSnackBar)

  const [manageDialogOpen, setManageDialogOpen] = useState(false)
  const [playerGroups, setPlayerGroups] = useState<TeamPlayerGroup[]>([])
  const [tabList, setTabList] = useState<TabItemType[]>([])
  const [tabIndex, setTabIndex] = useState<number>(0)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [dialogTitle, setDialogTitle] = useState<string>("")
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [text, setText] = useState("")
  const [searchText, setSearchText] = useState("")

  const { data, isLoading } = usePlayerGroupsQuery()

  const openMenu = Boolean(anchorEl)

  useEffect(() => {
    setSearchText(text)
  }, [text])

  useEffect(() => {
    if (data) {
      setPlayerGroups(data.teamPlayerGroups)
      const tabs = data.teamPlayerGroups.map((item, index) => ({
        label: item.name,
        value: index + 1,
        icon: item.playerCount ?? 0,
      }))
      const baseTab = [
        {
          label: t("all"),
          value: 0,
          icon: data?.totalPlayerCount || 0,
        },
      ]

      const newTabList = cloneDeep(baseTab).concat(tabs)
      setTabList(newTabList)
    }
  }, [data, t])

  const handleDialog = (title: string) => {
    setDialogTitle(title)
    setManageDialogOpen(!manageDialogOpen)
  }

  const handleChange = (event?: SyntheticEvent<Element, Event>, newValue?: string | number) => {
    if (!event) {
      setTabIndex(tabList.length)
    } else {
      setTabIndex(Number(newValue))
    }
  }

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleEdit = () => {
    handleDialog("editGroup")
    setAnchorEl(null)
  }

  const handleDelete = () => {
    setDeleteDialogOpen(!deleteDialogOpen)
    setAnchorEl(null)
  }

  const handleNewGroup = () => {
    // tabList 는 All 탭을 포함하므로 상한 판정은 실제 그룹 수(playerGroups)로 한다 (#167)
    if (playerGroups.length < MAX_PLAYER_GROUP_COUNT) {
      handleDialog("newGroup")
    } else {
      setSnackBar({
        open: true,
        message: t("messages.303"), // The number of player groups cannot exceed 50.
        severity: "error",
      })
    }
  }

  return (
    <div className="PlayerGroupCont">
      <div className="TabGroupsCont">
        <PlayerGroupTabs
          tabList={tabList}
          tabIndex={Math.min(tabIndex, tabList.length - 1)}
          handleDialog={handleNewGroup}
          handleChange={handleChange}
        />
      </div>
      <div className="TabHeadCont">
        <div className="tab-title">
          <span>{tabList[tabIndex]?.label}</span>
          {tabList[tabIndex]?.value > 0 ? (
            <IconButton
              type="ghost"
              icon="more-line"
              size="sm"
              onClick={handleClick}
              className={classNames({ openMenu })}
            />
          ) : null}
        </div>
        <Search onChange={(text) => setText(text)} className="player-group-search" />
      </div>
      <Menu
        className="PlayerGroupsMenuCont"
        anchorEl={anchorEl}
        open={openMenu}
        onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={handleEdit}>{t("button.edit")}</MenuItem>
        <MenuItem onClick={handleDelete}>{t("button.delete")}</MenuItem>
      </Menu>
      <PlayerGroupDeleteDialog
        open={deleteDialogOpen}
        idfTeamPlayerGroup={tabIndex > 0 ? playerGroups[tabIndex - 1]?.idfTeamPlayerGroup : 0}
        playerCount={tabIndex > 0 ? (playerGroups[tabIndex - 1]?.playerCount ?? 0) : 0}
        onClose={() => setDeleteDialogOpen(!deleteDialogOpen)}
        setTabIndex={setTabIndex}
      />
      <PlayerGroupManageDialog
        open={manageDialogOpen}
        title={dialogTitle}
        idfTeamPlayerGroup={tabIndex > 0 ? playerGroups[tabIndex - 1]?.idfTeamPlayerGroup : 0}
        defaultValue={dialogTitle === "editGroup" ? tabList[tabIndex]?.label : ""}
        onClose={() => handleDialog("newGroup")}
        handleChange={handleChange}
      />
      <PlayerGroupTable
        idfPlayerGroup={!tabIndex ? undefined : playerGroups[tabIndex - 1]?.idfTeamPlayerGroup}
        searchText={searchText}
        isLoading={isLoading}
      />
    </div>
  )
}
